import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    Typography,
    makeStyles,
} from '@material-ui/core';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DialogActions from 'components/widgets/modal/DialogActions';
import ButtonStyles from 'styles/theme/Button';

const useStyles = makeStyles((theme) => ({
    content: {
        paddingTop: theme.spacing(1),
        minHeight: 180,
    },
    hint: {
        fontSize: 12,
        color: theme.palette.grey[600],
        marginBottom: theme.spacing(1.5),
    },
    loadingBox: {
        minHeight: 160,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyBox: {
        minHeight: 140,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        color: theme.palette.grey[600],
        fontSize: 13,
        border: `1px dashed ${theme.palette.border.cloud}`,
        borderRadius: 8,
        padding: theme.spacing(2),
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        maxHeight: 360,
        overflowY: 'auto',
        paddingRight: 4,
    },
    bubble: {
        background: theme.palette.background.lavenderBlue || '#E8F1FB',
        border: `1px solid ${theme.palette.border.cloud}`,
        borderRadius: 12,
        borderBottomRightRadius: 4,
        padding: theme.spacing(1.25, 1.5),
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: 13,
        lineHeight: 1.45,
        color: theme.palette.grey[800],
    },
    bubbleMeta: {
        fontSize: 11,
        color: theme.palette.grey[600],
        marginBottom: 4,
        fontWeight: 600,
    },
    footerNote: {
        marginTop: theme.spacing(1.5),
        fontSize: 12,
        color: theme.palette.grey[600],
    },
    ...ButtonStyles.getStyle(theme),
}));

const PreviewReportDialog = ({
    open,
    onClose,
    loading,
    messages,
    totalCount,
    reportName,
}) => {
    const classes = useStyles();
    const hasMessages = messages.length > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogAppBar
                title={reportName ? `Preview — ${reportName}` : 'Report Template Preview'}
                onClose={onClose}
                iconSize="sm"
            />
            <DialogContent>
                <Box className={classes.content}>
                    {loading && (
                        <Box className={classes.loadingBox}>
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {!loading && !hasMessages && (
                        <Box className={classes.emptyBox}>
                            No preview available. The query returned no rows, or placeholders
                            could not be resolved.
                        </Box>
                    )}

                    {!loading && hasMessages && (
                        <>
                            <Box className={classes.list}>
                                {messages.map((message, index) => (
                                    <Box key={`preview-msg-${index + 1}`}>
                                        <Box className={classes.bubble}>
                                            {message}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                            {totalCount > messages.length && (
                                <Typography className={classes.footerNote}>
                                    {`Showing ${messages.length} of ${totalCount} messages from the query.`}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions
                titlePrimary="Close"
                hiddenSecondaryButton
                onClickPrimary={onClose}
                classPrimaryButton={classes.containedInfo}
            />
        </Dialog>
    );
};

PreviewReportDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    messages: PropTypes.arrayOf(PropTypes.string),
    totalCount: PropTypes.number,
    reportName: PropTypes.string,
};

PreviewReportDialog.defaultProps = {
    loading: false,
    messages: [],
    totalCount: 0,
    reportName: '',
};

export default PreviewReportDialog;
